// @flow

import './ApartmentContainer.css';
import _ from 'lodash/fp';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Redirect, withRouter} from 'react-router-dom';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {
  reset,
  fetchApartment,
  fetchProject,
  fetchDeviations,
  fetchApartmentConfigurations,
  createApartment,
  updateApartment,
  openApartmentDeviation,
  removeApartmentDeviation,
  addFile,
  removeFile,
  abortUpload,
  abortAllUploads,
  toggleCopyDialog,
  clear,
  updateFile,
  toggleRenameDialog,
  deleteFile,
} from '../../actions/apartmentView';
import {setDirty} from '../../actions/form';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import ApartmentForm from '../../components/ApartmentForm/ApartmentForm';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import ApartmentDeviationList from '../../components/ApartmentDeviationList/ApartmentDeviationList';
import ApartmentDeviationDialogContainer from '../ApartmentDeviationDialogContainer/ApartmentDeviationDialogContainer';
import CopyApartmentDialog from '../../components/CopyApartmentDialog/CopyApartmentDialog';
import FileDropzone from '../../components/FileDropzone/FileDropzone';
import BreadcrumbsContainer from '../BreadcrumbsContainer/BreadcrumbsContainer';
import FileList from '../../components/FileList/FileList';
import Navigation from '../../components/Navigation/Navigation';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import validator from '../../helpers/validator';
import DisableContent from '../../components/DisableContent/DisableContent';
import RenameFileDialog from '../../components/RenameFileDialog/RenameFileDialog';

const copyApartmentValidation = {
  objectId: {rule: 'string|required', label: 'Lägenhetsnummer'},
};

const apartmentValidation = {
  objectId: {rule: 'string|required', label: 'Lägenhetsnummer'},
  floor: {rule: 'string', label: 'Plan'},
  area: {rule: 'numeric', label: 'Yta'},
  address: {rule: 'string|required', label: 'Adress'},
  zipCode: {rule: 'string|required', label: 'Postnummer'},
  city: {rule: 'string|required', label: 'Stad'},
};

const fileRenameValidation = {
  name: {rule: 'string|required|max:255', label: 'Filnamn'},
};

const notArchived =
  _.filter({archived: null});

const includeId = id =>
  _.filter(t => t.id === id || !t.archived);

const filterConfigurations = apartment =>
  apartment
    ? includeId(apartment.apartmentConfigId)
    : notArchived;

export type Props = {
  history: Object,
  setDirty: typeof setDirty,
  reset: typeof reset,
  resetForm: typeof resetForm,
  isDirty: boolean,
  apartment: ?any,
  apartmentFormValues: any,
  apartmentFormIsValid: boolean,
  project: ?any,
  deviations: any[],
  apartmentDeviations: any[],
  configurations: any[],
  isFetching: boolean,
  params: any,
  error: ?any,
  orderPath: ?string,
  projectPath: string,
  resourcePath: string,
  fetchData: (_: {id?: any, projectId?: any}) => any,
  updateApartment: typeof updateApartment,
  touch: typeof touch,
  clear: typeof clear,
  openApartmentDeviation: typeof openApartmentDeviation,
  removeApartmentDeviation: typeof removeApartmentDeviation,
  projectId: ?number,
  apartmentUploads: Object,
  uploads: Object,
  abortAllUploads: typeof abortAllUploads,
  apartmentFiles: Object,
  createApartment: typeof createApartment,
  addFile: typeof addFile,
  removeFile: typeof removeFile,
  abortUpload: typeof abortUpload,
  toggleCopyDialog: typeof toggleCopyDialog,
  showCopyDialog: boolean,
  fileRenameFormValues: any,
  fileRenameFormIsValid: boolean,
  toggleRenameDialog: typeof toggleRenameDialog,
  showRenameDialog: false | 'contract' | 'floorPlan' | 'kitchenPlan',
  updateFile: typeof updateFile,
  deleteFile: typeof deleteFile,
  apartments: any[],
};

class ApartmentContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchData({
      id: this.props.params.id,
      projectId: this.props.projectId,
    });
  }

  componentWillUnmount() {
    this.props.abortAllUploads(this.props.apartmentUploads);
    this.props.clear();
  }

  setDirty() {
    const {
      setDirty,
      isDirty,
    } = this.props;

    if (!isDirty) {
      setDirty(true, 'apartment');
    }
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('apartment');
    setDirty(false, 'apartment');
  }

  copy({objectId}) {
    const {
      apartment,
      createApartment,
    } = this.props;

    if (!!apartment) {
      const newApartment = {
        ...apartment,
        objectId,
        contract: null,
      };
      createApartment(apartment.projectId, newApartment);
    }
  }

  save() {
    const {
      apartment,
      apartmentFormValues,
      apartmentFormIsValid,
      apartmentFiles,
      createApartment,
      updateApartment,
      apartmentDeviations,
      projectId,
      touch,
    } = this.props;
    const apartmentFields = _.keys(apartmentValidation);
    touch('apartment', ...apartmentFields);
    if (!apartmentFormIsValid) {
      return;
    }

    const data = {
      ...apartmentFormValues,
      ...apartmentFiles,
      deviations: apartmentDeviations,
    };

    if (!apartment && projectId) {
      createApartment(projectId, data);
    } else if (!!apartment) {
      updateApartment(apartment.id, data);
    }
  }

  saveFile() {
    const {
      fileRenameFormValues,
      fileRenameFormIsValid,
      apartmentFiles,
      updateFile,
      touch,
      showRenameDialog,
    } = this.props;
    if (!showRenameDialog) {
      return;
    }
    const fileRenameFields = _.keys(fileRenameValidation);
    touch('fileRename', ...fileRenameFields);

    const file = apartmentFiles[showRenameDialog];
    if (!fileRenameFormIsValid || !file) {
      return;
    }

    const data = _.pick(fileRenameFields, fileRenameFormValues);

    updateFile(file.id, showRenameDialog, data);
  }

  render() {
    const {
      history,
      isDirty,
      params: {id},
      project,
      orderPath,
      projectPath,
      resourcePath,
      apartment,
      error,
      isFetching,
      updateApartment,
      apartmentDeviations,
      deviations,
      configurations,
      openApartmentDeviation,
      removeApartmentDeviation,
      addFile,
      removeFile,
      abortUpload,
      apartmentFiles,
      apartmentUploads,
      uploads,
      apartmentFormValues,
      toggleCopyDialog,
      showCopyDialog,
      fileRenameFormValues,
      toggleRenameDialog,
      showRenameDialog,
      deleteFile,
      apartments,
      clear,
      fetchData,
      projectId,
    } = this.props;

    return (
      <div className="relative ApartmentContainer">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation
                current={apartment}
                list={apartments}
                prevPath={resourcePath}
                clear={clear}
                fetchData={id => fetchData({id, projectId})} />
              <div>
                <BreadcrumbsContainer project={project} />
                <div className="content-title">
                  {(apartment && apartment.objectId) || 'Ny lägenhet'}
                </div>
              </div>
            </div>
          </div>
          <div className="column shrink horizontal-button-list">
            {apartment &&
              <DisableContent disabled={isDirty}>
                <ActionConfirmation
                  id="archiveApartment"
                  className="secondary button"
                  message={`Vill du verkligen ${apartment.archived ? 'av' : ''}arkivera den här lägenheten?`}
                  buttonText={apartment.archived ? 'Avarkivera' : 'Arkivera'}
                  action={() => updateApartment(apartment.id, {
                    archived: !apartment.archived,
                  })} />
              </DisableContent>
            }
            {apartment &&
              <button
                type="button"
                className="secondary button"
                onClick={() => toggleCopyDialog(true)} >
                Skapa kopia
              </button>
            }
          </div>
        </div>
        <LoadingIndicator isLoading={isFetching} />
        {!showCopyDialog &&
          <ServerError error={error} />
        }
        <div className="content-section box">
          <div className="row">
            <div className="column small-8">
              {(apartment && !!apartment.orderId && orderPath) ?
                <ApartmentForm
                  initialValues={apartment}
                  apartment={apartment}
                  formValues={apartmentFormValues}
                  configurations={filterConfigurations(apartment)(configurations)}
                  onUserChange={() => this.setDirty()}
                  onOpenOrder={() => history.push(`${orderPath}/${apartment.orderId}`)}
                  validate={values => validator(apartmentValidation, values)} />
                :
                <ApartmentForm
                  initialValues={apartment}
                  configurations={filterConfigurations(apartment)(configurations)}
                  onUserChange={() => this.setDirty()}
                  validate={values => validator(apartmentValidation, values)} />
              }
            </div>
            <div className="column small-4">
              <FileDropzone
                label="Beställningsunderlag"
                file={apartmentFiles.contract}
                upload={uploads.contract}
                onUpload={f => {
                  addFile('contract', f);
                  this.setDirty();
                }}
                onUploadAbort={() => {
                  abortUpload(apartmentUploads.contract);
                  this.setDirty();
                }}
                onRemove={() => {
                  removeFile('contract');
                  this.setDirty();
                }} />
              <FileDropzone
                label="Planritning"
                file={apartmentFiles.floorPlan}
                upload={uploads.floorPlan}
                editable={!isDirty}
                onEdit={() => toggleRenameDialog('floorPlan')}
                onUpload={f => {
                  addFile('floorPlan', f);
                  this.setDirty();
                }}
                onUploadAbort={() => {
                  abortUpload(apartmentUploads.floorPlan);
                  this.setDirty();
                }}
                onRemove={() => {
                  removeFile('floorPlan');
                  this.setDirty();
                }} />
              <FileDropzone
                label="Köksritning"
                file={apartmentFiles.kitchenPlan}
                upload={uploads.kitchenPlan}
                editable={!isDirty}
                onEdit={() => toggleRenameDialog('kitchenPlan')}
                onUpload={f => {
                  addFile('kitchenPlan', f);
                  this.setDirty();
                }}
                onUploadAbort={() => {
                  abortUpload(apartmentUploads.kitchenPlan);
                  this.setDirty();
                }}
                onRemove={() => {
                  removeFile('kitchenPlan');
                  this.setDirty();
                }} />
            </div>
          </div>
        </div>
        <div className="content-section box">
          <div className="row">
            <div className="small-6 column">
              {!!apartmentFormValues && (
                <button
                  onClick={() => history.push(
                    `${projectPath}/apartmentconfigurations/${apartmentFormValues.apartmentConfigId}`,
                  )}
                  type="button"
                  className="button small expanded">
                  Öppna konfiguration
                </button>
              )}
            </div>
            <div className="small-6 column">
              <ApartmentDeviationList
                list={apartmentDeviations}
                deviations={deviations}
                onSelectItem={d => openApartmentDeviation(d)}
                onRemoveItem={d => removeApartmentDeviation(d.id)} />
              <hr />
              <button
                onClick={() => openApartmentDeviation()}
                type="button"
                className="button small expanded">
                Lägg till
              </button>
              <ApartmentDeviationDialogContainer />
            </div>
          </div>
        </div>
        {apartment &&
          <div className="content-section box">
            <label className="headlabel">Arkiverade dokument</label>
            <label className="description">
              Nedan listas arkiverade dokument. Dokument arkiveras automatiskt, och listas nedan,
              när de skrivs över av en ny uppladdning.
            </label>
            <div className="row">
              <div className="small-4 column">
                <FileList
                  label="Arkiverade beställningsunderlag"
                  list={apartment.oldContracts}
                  disabled={isDirty}
                  onRemove={file => deleteFile(apartment.id, file)} />
              </div>
              <div className="small-4 column">
                <FileList
                  label="Arkiverade planritningar"
                  list={apartment.oldFloorPlans}
                  disabled={isDirty}
                  onRemove={file => deleteFile(apartment.id, file)} />
              </div>
              <div className="small-4 column">
                <FileList
                  label="Arkiverade köksritningar"
                  list={apartment.oldKitchenPlans}
                  disabled={isDirty}
                  onRemove={file => deleteFile(apartment.id, file)} />
              </div>
            </div>
          </div>
        }
        {apartment && apartment.id !== _.toNumber(id) &&
          <Redirect to={`${resourcePath}/${apartment.id}`} />
        }
        {isDirty &&
          <SaveMenu
            isLoading={isFetching}
            onDiscard={() => this.reset()}
            onSave={() => this.save()} />
        }
        {showCopyDialog && !!apartment &&
          <CopyApartmentDialog
            apartment={apartment}
            isLoading={isFetching}
            error={error}
            onSubmit={values => this.copy(values)}
            validate={values => validator(copyApartmentValidation, values)}
            onClose={() => toggleCopyDialog(false)}/>
        }
        {showRenameDialog && !!apartmentFiles[showRenameDialog] &&
          <RenameFileDialog
            error={error}
            file={apartmentFiles[showRenameDialog]}
            formValues={fileRenameFormValues}
            isLoading={isFetching}
            onSubmit={() => this.saveFile()}
            onClose={() => toggleRenameDialog(false)}
            validate={values => validator(fileRenameValidation, values)} />
        }
      </div>
    );
  }
}

const getUploads = (uploads, files) =>
  _.mapValues(u => files.byId[u], uploads) || {};

const mapStateToProps = ({
  apartmentView: {
    apartment: {
      active: apartment,
      error,
      isDirty,
      isFetching,
      deviations: apartmentDeviations,
      files: apartmentFiles,
      uploads: apartmentUploads,
    },
    project: {
      active: project,
    },
    deviations: {
      list: deviations,
    },
    configurations: {
      list: configurations,
    },
    copyDialogVisibility: showCopyDialog,
    fileRenameDialogVisibility: showRenameDialog,
  },
  projectView: {
    filteredApartments: apartments,
  },
  form,
  files,
}) => ({
  isDirty,
  apartment,
  project,
  apartmentDeviations,
  configurations,
  error,
  isFetching,
  deviations,
  apartmentFiles,
  apartmentUploads,
  showCopyDialog,
  uploads: getUploads(apartmentUploads, files),
  apartmentFormIsValid: isValid('apartment')({form}),
  apartmentFormValues: getFormValues('apartment')({form}),
  showRenameDialog,
  fileRenameFormIsValid: isValid('fileRename')({form}),
  fileRenameFormValues: getFormValues('fileRename')({form}),
  apartments,
});

const mapDispatchToProps = dispatch => ({
  setDirty: dirty =>
    dispatch(setDirty(dirty, 'apartment')),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
  fetchData: ({id, projectId}) => {
    dispatch(fetchDeviations());
    if (id) {
      dispatch(fetchApartment(id));
    } else if (projectId) {
      dispatch(fetchProject(projectId));
      dispatch(fetchApartmentConfigurations(projectId));
    }
  },
  createApartment: (projectId, data) =>
    dispatch(createApartment(projectId, data)),
  updateApartment: (id, data) =>
    dispatch(updateApartment(id, data)),
  openApartmentDeviation: deviation =>
    dispatch(openApartmentDeviation(deviation)),
  removeApartmentDeviation: deviationId =>
    dispatch(removeApartmentDeviation(deviationId)),
  removeFile: name =>
    dispatch(removeFile(name)),
  abortUpload: tempId =>
    dispatch(abortUpload(tempId)),
  abortAllUploads: uploads =>
    dispatch(abortAllUploads(uploads)),
  addFile: (name, [file, ...rest]) => {
    if (file) {
      dispatch(addFile(name, file));
    }
  },
  toggleCopyDialog: show =>
    dispatch(toggleCopyDialog(show)),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  clear: () =>
    dispatch(clear()),
  updateFile: (id, name: 'contract' | 'floorPlan' | 'kitchenPlan', data) =>
    dispatch(updateFile(id, name, data)),
  toggleRenameDialog: (show: false | 'contract' | 'floorPlan' | 'kitchenPlan') =>
    dispatch(toggleRenameDialog(show)),
  deleteFile: (apartmentId, file) =>
    dispatch(deleteFile(apartmentId, file)),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(ApartmentContainer));



// WEBPACK FOOTER //
// ./src/containers/ApartmentContainer/ApartmentContainer.js